import { useState, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Clock, AlertCircle } from 'lucide-react'
import { useSpace, useIssues, useStatuses, useIssueMutations } from '../hooks'
import { cn, toDateKey } from '../utils'
import { IssueModal } from '../components/IssueModal'
import { NewIssueModal } from '../components/NewIssueModal'
import { getIssueErrorMessage } from '../api'
import { PRIORITY_OPTIONS } from '../boardUtils'
import type { Issue } from '../api'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import { cardClassName } from '@jira-clone/ui'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MAX_VISIBLE_PER_DAY = 3

function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  const start = new Date(first)
  start.setDate(first.getDate() - first.getDay())
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
}

export function CalendarPage() {
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedIssueId = searchParams.get('selectedIssue')

  const { data: space } = useSpace(spaceId ?? null)
  const { data: issues = [], isLoading } = useIssues(spaceId ?? null)
  const { data: statuses = [] } = useStatuses(spaceId ?? null)
  const { data: userOptions = [] } = useUserOptions(spaceId ?? null)
  const { create, update, remove } = useIssueMutations(spaceId ?? null)

  const [cursor, setCursor] = useState(() => new Date())
  const [modalOpen, setModalOpen] = useState(false)
  const [newIssueModalOpen, setNewIssueModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [newTitle, setNewTitle] = useState('')
  const [newPriority, setNewPriority] = useState<Issue['priority']>('medium')

  const selectedIssue = useMemo(() => {
    if (!selectedIssueId) return null
    return (issues as Issue[]).find((i) => i.id === selectedIssueId || i.key === selectedIssueId) ?? null
  }, [issues, selectedIssueId])

  const handleOpenIssue = (issueId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', issueId)
      return next
    })
  }

  const handleCloseIssueModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }

  const year = cursor.getFullYear()
  const month = cursor.getMonth()
  const days = useMemo(() => buildMonthGrid(year, month), [year, month])
  const monthLabel = cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const todayKey = toDateKey(new Date())

  const issuesByDay = useMemo(() => {
    const map = new Map<string, Issue[]>()
    for (const issue of issues) {
      if (!issue.due_date) continue
      const key = toDateKey(new Date(issue.due_date))
      const list = map.get(key) ?? []
      list.push(issue)
      map.set(key, list)
    }
    return map
  }, [issues])

  const goToPreviousMonth = () => setCursor(new Date(year, month - 1, 1))
  const goToNextMonth = () => setCursor(new Date(year, month + 1, 1))
  const goToToday = () => setCursor(new Date())

  const handleOpenCreateForDate = (dateStr: string) => {
    setSelectedDate(dateStr)
    setNewTitle('')
    setModalOpen(true)
  }

  const handleCreateIssue = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim()) return
    try {
      await create.mutateAsync({
        title: newTitle.trim(),
        priority: newPriority,
        due_date: selectedDate ? new Date(selectedDate).toISOString() : undefined,
      })
      setModalOpen(false)
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'create tasks'))
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto p-6 bg-white dark:bg-slate-950 font-sans">
      {/* Calendar Controls Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-blue-600" />
            Calendar View
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {issues.filter((i: any) => i.due_date).length} scheduled tasks for {space?.name || 'Space'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-900 shadow-xs">
            <button
              onClick={goToPreviousMonth}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800"
              title="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={goToToday}
              className="px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Today
            </button>
            <button
              onClick={goToNextMonth}
              className="rounded p-1 text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800"
              title="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <span className="text-sm font-bold text-gray-900 dark:text-slate-100 min-w-[130px] text-center">{monthLabel}</span>
          <button
            onClick={() => handleOpenCreateForDate(todayKey)}
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Task
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className={cn(cardClassName(), 'overflow-hidden')}>
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50/80 dark:border-slate-800 dark:bg-slate-900/50">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              className="border-r border-gray-200 px-3 py-2 text-center text-xs font-bold text-gray-600 last:border-r-0 dark:border-slate-800 dark:text-slate-300"
            >
              {w}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((d) => {
            const key = toDateKey(d)
            const isToday = key === todayKey
            const isCurrentMonth = d.getMonth() === month
            const dayIssues = issuesByDay.get(key) ?? []
            const visible = dayIssues.slice(0, MAX_VISIBLE_PER_DAY)
            const overflow = dayIssues.length - visible.length

            return (
              <div
                key={key}
                onClick={() => handleOpenCreateForDate(key)}
                className={cn(
                  'min-h-[110px] border-b border-r border-gray-200 p-2 transition-colors cursor-pointer dark:border-slate-800 last:border-r-0 hover:bg-blue-50/20 dark:hover:bg-blue-950/20',
                  !isCurrentMonth && 'bg-gray-50/60 dark:bg-slate-950/40 text-gray-400 dark:text-slate-600'
                )}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span
                    className={cn(
                      'inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                      isToday
                        ? 'bg-blue-600 font-bold text-white shadow-xs'
                        : isCurrentMonth
                        ? 'text-gray-700 dark:text-slate-300'
                        : 'text-gray-400 dark:text-slate-600'
                    )}
                  >
                    {d.getDate()}
                  </span>
                </div>

                <div className="space-y-1">
                  {visible.map((issue) => (
                    <div
                      key={issue.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenIssue(issue.id)
                      }}
                      className={cn(
                        'truncate rounded border px-1.5 py-0.5 text-[11px] font-medium shadow-xs cursor-pointer hover:ring-1 hover:ring-blue-400 transition-all',
                        issue.status_category === 'done'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 line-through opacity-80'
                          : issue.priority === 'critical' || issue.priority === 'high'
                          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300'
                          : 'border-gray-200 bg-white text-gray-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200'
                      )}
                      title={`${issue.key}: ${issue.title} (Click to open)`}
                    >
                      <span className="font-mono text-[10px] mr-1 font-semibold opacity-80">{issue.key}</span>
                      {issue.title}
                    </div>
                  ))}
                  {overflow > 0 && (
                    <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 px-1">
                      +{overflow} more
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Add Task Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900 border border-gray-200 dark:border-slate-800">
            <h3 className="text-base font-bold text-gray-900 dark:text-slate-100">Schedule Task</h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Due date: {selectedDate}</p>

            <form onSubmit={handleCreateIssue} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  autoFocus
                  required
                  placeholder="Task title..."
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
                  Priority
                </label>
                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as Issue['priority'])}
                  className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {[...PRIORITY_OPTIONS].reverse().map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={create.isPending}
                  className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50 shadow-xs"
                >
                  {create.isPending ? 'Adding...' : 'Schedule Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deep-linked Issue Modal */}
      {selectedIssue && (
        <IssueModal
          issue={selectedIssue}
          open={!!selectedIssue}
          onClose={handleCloseIssueModal}
          statuses={statuses}
          members={userOptions}
          epics={(issues as Issue[]).filter((i) => i.type === 'epic')}
          onUpdate={async (id, payload) => {
            await update.mutateAsync({ id, payload })
          }}
          onDelete={async (id) => {
            await remove.mutateAsync(id)
            handleCloseIssueModal()
          }}
        />
      )}

      {/* New Issue Modal */}
      {spaceId && (
        <NewIssueModal
          open={newIssueModalOpen}
          onClose={() => setNewIssueModalOpen(false)}
          spaceId={spaceId}
        />
      )}
    </div>
  )
}
